import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";

import {
  Box,
  Container,
  FormControlLabel,
  Grid,
  Typography,
  useTheme,
} from "@mui/material";
import { PagesID } from "@utils/utilsConfigurations";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { Association, ColumnsType } from "src/interfaces/Common";
import {
  GetLeaderboardClusterData,
  UpdateScoringLeaderboardClusterBody,
} from "../types";
import { defaultValidation } from "@utils/utilsValidators";
import { ColumnInterface } from "@components/Table/types";
import AssociationTableCst from "@components/AssociationTableCst";
import ButtonCst from "@components/ButtonCst";
import {
  InputCst,
  InputAssociationChipsCst,
  RadioCst,
} from "@components/InputCst";
import { MaterialUISwitch } from "../style";
import CustomTopToolbar from "@components/Table/components/CustomTopToolbar";

const ScoringCluster: React.FC<{ page: PagesID }> = ({ page }) => {
  const theme = useTheme();
  const { t } = useTranslation(["leaderboard-cluster-page", "form"]);
  const navigate = useNavigate();
  const { id, initiativeId, clusterId } = useParams();
  const [inputForm, setInputForm] = useState<any>({});
  const [errorInputForm, setErrorInputForm] = React.useState<any>({});
  const [enabled, setEnabled] = useState<{
    learningObjectsEnabled: boolean;
    scoringTalkEnabled: boolean;
    scoringPostEnabled: boolean;
    scoringShareEnabled: boolean;
    scoringCommentEnabled: boolean;
    scoringLikeEnabled: boolean;
  }>({
    learningObjectsEnabled: true,
    scoringTalkEnabled: true,
    scoringPostEnabled: true,
    scoringShareEnabled: true,
    scoringCommentEnabled: true,
    scoringLikeEnabled: true,
  });
  const learningObjectsOptions = [
    {
      label: t("scoring.all_los"),
      value: true,
      disabled: !enabled.learningObjectsEnabled,
    },
    {
      label: t("scoring.selected_los"),
      value: false,
      disabled: !enabled.learningObjectsEnabled,
    },
  ];
  const communitiesOptions = [
    {
      label: t("scoring.all_communities"),
      value: true,
    },
    {
      label: t("scoring.selected_communities"),
      value: false,
    },
  ];
  const [openAssociations, setOpenAssociations] = useState<
    boolean | Association
  >(false);
  const [learningObjectsAssociation, setLearningObjectsAssociation] = useState<
    { id: number; label: string }[]
  >([]);
  const [communitiesAssociation, setCommunitiesAssociation] = useState<
    { id: number; label: string }[]
  >([]);

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  const { columns, dataDetail, mutate } = useFetchDataDetail(
    ServicesURL.getLeaderboardCluster
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!)
      .replace(":clusterId", clusterId!),
    PagesID["corporate.initiative.leaderboard-cluster.scoring"],
  );

  const { columns: columnsCommunity } = useFetchDataDetail(
    "",
    PagesID[
      "corporate.initiative.leaderboard-cluster.scoring.association.community"
    ],
  );

  const columnsByAccessorKey = columns?.reduce<{ [k: string]: ColumnsType }>(
    (agg, cur) => {
      agg[cur.accessorKey] = cur;
      return agg;
    },
    {},
  );

  const onConfirm = () => {
    const { canBeSubmit, showError } = defaultValidation(columns!, inputForm);
    setErrorInputForm(showError);
    let stringError = "";
    const findErrorCol = columns!.map((el: ColumnInterface) => {
      if (el.error) {
        stringError = el.error;
        return {
          [el.accessorKey]: {
            msg: el.error,
          },
        };
      } else {
        return;
      }
    });
    const mergeErrorArray = Object.assign({}, ...findErrorCol);
    if (Object.keys(mergeErrorArray).length > 0) {
      setErrorInputForm({ ...showError, ...mergeErrorArray });
    }
    if (canBeSubmit) {
      onSubmit(inputForm);
    }
  };

  const getErrMsg = (accessorKey: string): string => {
    return errorInputForm[accessorKey]?.msg || errorInputForm[accessorKey];
  };

  const getInitialData = () => {
    let initialState: any = {};

    const data = dataDetail as GetLeaderboardClusterData | undefined;
    const communities =
      data?.community_clusters && data.community_clusters.length
        ? data.community_clusters.map((community) => ({
            id: community.id,
            label: community.name,
          }))
        : undefined;
    const learning_objects =
      data?.scoring_learning_objects &&
      !data?.scoring_learning_objects?.find((lo) => !lo.lo_id)
        ? data.scoring_learning_objects.map(({ lo_id: id, title: label }) => ({
            id,
            label,
          }))
        : undefined;
    initialState = {
      communities,
      learning_objects,
      talk_score: data?.scoring_community?.talk_score,
      post_score: data?.scoring_community?.post_score,
      like_score: data?.scoring_community?.like_score,
      share_score: data?.scoring_community?.share_score,
      comment_score: data?.scoring_community?.comment_score,
    };
    return initialState;
  };

  const customRenderTopToolbar = ({
    table,
  }: {
    table: any;
  }): React.ReactNode => {
    return columnsCommunity ? (
      <CustomTopToolbar
        id={page + initiativeId}
        key={`custom_top_toolbar_${page}`}
        columns={columnsCommunity
          .filter((column) => (column as any)["hasFilter"])
          .map((column) => ({
            ...column,
            visibility: true,
            visibilityInTable: true,
          }))}
        configurationTopToolbar={[]}
        table={table}
        hiddenFilters={false}
        resetFormFilters={undefined}
        setHideHeader={undefined}
        counterElements={false}
        onToolbarChange={() => {}}
        modalTitle={undefined}
        noSummarySelected={undefined}
        filtersException={undefined}
        enableFullScreenToggle={false}
      />
    ) : (
      <></>
    );
  };

  useEffect(() => {
    if (dataDetail) {
      const initialData = getInitialData();
      setInputForm(initialData);
      setLearningObjectsAssociation(initialData.learning_objects);
      setCommunitiesAssociation(initialData.communities);
      setEnabled({
        learningObjectsEnabled:
          !Array.isArray(initialData.learning_objects) ||
          !!initialData.learning_objects.length,
        scoringCommentEnabled: !!initialData.comment_score,
        scoringLikeEnabled: !!initialData.like_score,
        scoringPostEnabled: !!initialData.post_score,
        scoringShareEnabled: !!initialData.share_score,
        scoringTalkEnabled: !!initialData.talk_score,
      });
    }
  }, [dataDetail]);

  const setOpenAssociation = (accessorKey: string, value: boolean) => {
    switch (accessorKey) {
      case "learning_objects":
        setOpenAssociations(
          value
            ? {
                accessorKey: "learning_objects",
                mediaType: "learning-objects",
                type: "table",
                titleKey: "",
                pageId:
                  PagesID[
                    "corporate.initiative.leaderboard-cluster.scoring.association.learning-object"
                  ],
                service: `${ServicesURL.learningObjectsStandAlone.replace(":corporateId", id!)}`,
              }
            : value,
        );
        break;
      case "communities":
        setOpenAssociations(
          value
            ? {
                accessorKey: "communities",
                mediaType: "communities",
                type: "table",
                titleKey: "",
                pageId:
                  PagesID[
                    "corporate.initiative.leaderboard-cluster.scoring.association.community"
                  ],
                service: ServicesURL.getCommunityClusters
                  .replace(":corporateId", id!)
                  .replace(":initiativeId", initiativeId!),
                multiChoice: false,
              }
            : value,
        );
        break;
    }
  };

  const getValues = (accessorKey: string) => {
    switch (accessorKey) {
      case "learning_objects":
        return learningObjectsAssociation;
      case "communities":
        return communitiesAssociation;
    }
  };

  const handleDelete = (accessorKey: string, e: any) => {
    switch (accessorKey) {
      case "learning_objects":
        setLearningObjectsAssociation(
          learningObjectsAssociation.filter(
            (element: any) => element.id !== e.id,
          ),
        );
        setInputForm({
          ...inputForm,
          learning_objects: learningObjectsAssociation
            .filter((element: any) => element.id !== e.id)
            .map(({ id }) => id),
        });
        break;
      case "communities":
        setCommunitiesAssociation(
          communitiesAssociation.filter((element: any) => element.id !== e.id),
        );
        setInputForm({
          ...inputForm,
          communities: communitiesAssociation
            .filter((element: any) => element.id !== e.id)
            .map(({ id }) => id),
        });
    }
  };

  const handleAssociation = (accessorKey: string, value: any) => {
    switch (accessorKey) {
      case "learning_objects":
        const updatedLearningObjectsAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return learningObjectsAssociation.find(
                (elem: any) => elem.id === +key,
              );
            }
          },
        );
        setLearningObjectsAssociation(updatedLearningObjectsAssociation);
        setInputForm({
          ...inputForm,
          learning_objects: updatedLearningObjectsAssociation.map(
            ({ id }: { id: number }) => id,
          ),
        });
        break;
      case "communities":
        const updatedCommunitiesAssociation: any = Object.keys(value).map(
          (key: string) => {
            if (key.includes("||")) {
              return {
                id: key.split("||")[0],
                label: key.split("||")[1],
              };
            } else {
              return learningObjectsAssociation.find(
                (elem: any) => elem.id === +key,
              );
            }
          },
        );
        setCommunitiesAssociation(updatedCommunitiesAssociation);
        setInputForm({
          ...inputForm,
          communities: updatedCommunitiesAssociation.map(
            ({ id }: { id: number }) => id,
          ),
        });
        break;
    }
  };

  const { trigger: triggetUpdateLeaderboardCluster } = useSWRMutation(
    ServicesURL.updateLeaderboardClusterScoring
      .replace(":corporateId", id!)
      .replace(":initiativeId", initiativeId!)
      .replace(":clusterId", clusterId!),
    (url: string, arg: { arg: any }) =>
      fetcher<any>(`${url}`, MethodType.PUT, { ...arg.arg }),
  );

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const scoringBody: UpdateScoringLeaderboardClusterBody = {
      community_clusters: inputForm.communities ?? [],
      scoring_community: {
        cluster_id: +clusterId!,
        comment_score: enabled.scoringCommentEnabled
          ? inputForm.comment_score
          : undefined,
        like_score: enabled.scoringLikeEnabled
          ? inputForm.like_score
          : undefined,
        post_score: enabled.scoringPostEnabled
          ? inputForm.post_score
          : undefined,
        share_score: enabled.scoringShareEnabled
          ? inputForm.share_score
          : undefined,
        talk_score: enabled.scoringTalkEnabled
          ? inputForm.talk_score
          : undefined,
      },
      scoring_learning_objects: enabled.learningObjectsEnabled
        ? !Array.isArray(inputForm.learning_objects)
          ? [null]
          : inputForm.learning_objects
        : [],
    };

    try {
      await triggetUpdateLeaderboardCluster(scoringBody);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
    }
    mutate && mutate();
  };

  const resetInputForm = () => {
    const initialData = getInitialData();
    setInputForm(initialData);
    setLearningObjectsAssociation(initialData.learning_objects);
    setCommunitiesAssociation(initialData.communities);
    setEnabled({
      learningObjectsEnabled:
        !Array.isArray(initialData.learning_objects) ||
        !!initialData.learning_objects.length,
      scoringCommentEnabled: !!initialData.comment_score,
      scoringLikeEnabled: !!initialData.like_score,
      scoringPostEnabled: !!initialData.post_score,
      scoringShareEnabled: !!initialData.share_score,
      scoringTalkEnabled: !!initialData.talk_score,
    });
  };

  let title = t("scoring.hero-title");
  if (openAssociations) {
    if ((openAssociations as Association).accessorKey == "learning_objects") {
      title = t("scoring.learning_objects_association.hero-title");
    }
    if ((openAssociations as Association).accessorKey == "communities") {
      title = t("scoring.communities_association.hero-title");
    }
  }

  return (
    <>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-community.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={title} description={""} />
        <Box
          sx={{
            mt: "25px",
            "&>.MuiGrid-root.MuiGrid-container>.MuiGrid-root.MuiGrid-item.MuiGrid-grid-xs-12>.MuiBox-root":
              {
                justifyContent: "flex-start",
                ".MuiGrid-root.MuiGrid-container:last-child": {
                  marginTop: "auto",
                },
              },
          }}
        >
          {!openAssociations && (
            <Grid container>
              <Grid item xs={12} lg={12} xl={12}>
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    padding: "32px",
                    minHeight: "495px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid key={"learningObjectsEnabled"} item xs={12} sm={12}>
                      <FormControlLabel
                        control={
                          <MaterialUISwitch
                            key={"learningObjectsEnabled"}
                            id={"learningObjectsEnabled"}
                            value={enabled.learningObjectsEnabled}
                            checked={enabled.learningObjectsEnabled}
                            required={true}
                            onChange={(e: any) => {
                              if (!e.target.checked) {
                                setInputForm({
                                  ...inputForm,
                                  learning_objects: undefined,
                                });
                                setLearningObjectsAssociation([]);
                              }
                              setEnabled({
                                ...enabled,
                                learningObjectsEnabled: e.target.checked,
                              });
                            }}
                          />
                        }
                        label={t("scoring.lo_scoring_enabled")}
                        labelPlacement="start"
                        sx={{
                          "&.MuiFormControlLabel-root": {
                            margin: "0 0 0 10px",
                            width: "253px",
                            justifyContent: "space-between",
                            fontWeight: "700",
                          },
                          "& .MuiTypography-root": {
                            fontWeight: "700",
                          },
                        }}
                      />
                    </Grid>
                    <Grid key={"learningObjectsRadio"} item xs={12} sm={12}>
                      <RadioCst
                        key={"lo"}
                        id={"lo"}
                        sx={{
                          width: "100%",
                          "& .MuiFormControlLabel-root": {
                            margin: "0 10px 0px 10px",
                            border: `1px solid ${theme.palette.divider}`,
                            flex: 1,
                            whiteSpace: "nowrap",
                            paddingX: "20px",
                            borderRadius: "3px",
                            textAlign: "start",
                          },
                        }}
                        value={
                          !Array.isArray(inputForm.learning_objects) ||
                          !enabled.learningObjectsEnabled
                        }
                        onChange={(e: any) => {
                          const isChecked = e === true || e === "true";
                          setInputForm({
                            ...inputForm,
                            learning_objects: !isChecked ? [] : undefined,
                          });
                          if (isChecked) {
                            setLearningObjectsAssociation([]);
                          }
                        }}
                        options={learningObjectsOptions}
                        disabled={!enabled.learningObjectsEnabled}
                      />
                    </Grid>
                    <Grid key={"spacer_learning_objects"} item xs={0} sm={6} />
                    <Grid
                      key={"learning_objects"}
                      item
                      xs={12}
                      sm={6}
                      sx={{
                        "&.MuiGrid-root": {
                          paddingTop: "0.71875rem",
                        },
                      }}
                    >
                      <InputAssociationChipsCst
                        key={"learning_objects"}
                        id={"learning_objects"}
                        label={
                          columnsByAccessorKey?.["learning_objects"].header
                        }
                        value={getValues("learning_objects")}
                        error={!!errorInputForm["learning_objects"]}
                        errorMessage={
                          t(`error.field.${getErrMsg("learning_objects")}`) ||
                          "Error"
                        }
                        required={true}
                        onDelete={(e) => handleDelete("learning_objects", e)}
                        onRedirect={() =>
                          setOpenAssociation("learning_objects", true)
                        }
                        disabled={
                          !Array.isArray(inputForm.learning_objects) ||
                          !enabled.learningObjectsEnabled
                        }
                        sx={{
                          "&.MuiBox-root": {
                            marginRight: "0.625rem",
                          },
                        }}
                      />
                    </Grid>
                    <Grid
                      key={"community_section"}
                      item
                      xs={12}
                      sm={12}
                      sx={{
                        fontWeight: 700,
                        marginLeft: "10px",
                      }}
                    >
                      {t("scoring.community_scoring")}
                    </Grid>
                    <Grid key={"communitiesRadio"} item xs={12} sm={12}>
                      <RadioCst
                        key={"communitiesRadio"}
                        id={"communitiesRadio"}
                        sx={{
                          width: "100%",
                          "& .MuiFormControlLabel-root": {
                            margin: "0 10px 0px 10px",
                            border: `1px solid ${theme.palette.divider}`,
                            flex: 1,
                            whiteSpace: "nowrap",
                            paddingX: "20px",
                            borderRadius: "3px",
                            textAlign: "start",
                          },
                        }}
                        value={!Array.isArray(inputForm.communities)}
                        onChange={(e: any) => {
                          const isChecked = e === true || e === "true";
                          setInputForm({
                            ...inputForm,
                            communities: !isChecked ? [] : undefined,
                          });
                          if (isChecked) {
                            setCommunitiesAssociation([]);
                          }
                        }}
                        options={communitiesOptions}
                      />
                    </Grid>
                    <Grid key={"spacer_communities"} item xs={0} sm={6} />
                    <Grid
                      key={"communities"}
                      item
                      xs={12}
                      sm={6}
                      sx={{
                        "&.MuiGrid-root": {
                          paddingTop: "0.71875rem",
                        },
                      }}
                    >
                      <InputAssociationChipsCst
                        key={"communities"}
                        id={"communities"}
                        label={columnsByAccessorKey?.["communities"].header}
                        value={getValues("communities")}
                        error={!!errorInputForm["communities"]}
                        errorMessage={
                          t(`error.field.${getErrMsg("communities")}`) ||
                          "Error"
                        }
                        required={true}
                        onDelete={(e) => handleDelete("communities", e)}
                        onRedirect={() =>
                          setOpenAssociation("communities", true)
                        }
                        disabled={!Array.isArray(inputForm.communities)}
                        sx={{
                          "&.MuiBox-root": {
                            marginRight: "0.625rem",
                          },
                        }}
                      />
                    </Grid>
                    <Grid
                      key={"talk_score"}
                      item
                      xs={12}
                      sm={12}
                      sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: "6.1875rem",
                        alignContent: "center",
                        marginLeft: "10px",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <MaterialUISwitch
                            key={"scoringTalkEnabled"}
                            id={"scoringTalkEnabled"}
                            value={enabled.scoringTalkEnabled}
                            checked={enabled.scoringTalkEnabled}
                            onChange={(e: any) => {
                              if (!e.target.checked) {
                                setInputForm((inputForm: any) => ({
                                  ...inputForm,
                                  talk_score: undefined,
                                }));
                              }
                              setEnabled((enabled) => ({
                                ...enabled,
                                scoringTalkEnabled: e.target.checked,
                              }));
                            }}
                          />
                        }
                        label={t("scoring.talk_scoring_enabled")}
                        labelPlacement="end"
                        sx={{
                          "&.MuiFormControlLabel-root": {
                            margin: 0,
                            width: "133px",
                            // justifyContent: "space-between",
                            gap: "2.0625rem",
                            fontWeight: "700",
                          },
                          "& .MuiTypography-root": {
                            fontWeight: "700",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "nowrap",
                          gap: "2.0625rem",
                          alignItems: "center",
                        }}
                      >
                        <InputCst
                          key={"talk_score"}
                          id={"talk_score"}
                          value={inputForm.talk_score}
                          disabled={!enabled.scoringTalkEnabled}
                          onChange={(e) => {
                            setInputForm((inputForm: any) => ({
                              ...inputForm,
                              talk_score: e.target.value,
                            }));
                          }}
                          label={columnsByAccessorKey?.["talk_score"].header}
                          sx={{
                            "& .MuiInputBase-root .MuiInputBase-input": {
                              paddingLeft: theme.spacing(1.875),
                              paddingTop: theme.spacing(1.625),
                              paddingBottom: theme.spacing(1.625),
                              lineHeight: theme.spacing(2.875),
                            },
                            "& .MuiInputLabel-root[data-shrink='false']": {
                              top: "50%",
                              transform: "translateY(-50%)",
                            },
                            "& .MuiFormHelperText-root": {
                              margin: 0,
                            },
                          }}
                          type="number"
                        />
                        <Typography>
                          {t("scoring.talk_scoring_enabled_after")}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      key={"post_score"}
                      item
                      xs={12}
                      sm={12}
                      sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: "6.1875rem",
                        alignContent: "center",
                        marginLeft: "10px",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <MaterialUISwitch
                            key={"scoringPostEnabled"}
                            id={"scoringPostEnabled"}
                            value={enabled.scoringPostEnabled}
                            checked={enabled.scoringPostEnabled}
                            onChange={(e: any) => {
                              if (!e.target.checked) {
                                setInputForm((inputForm: any) => ({
                                  ...inputForm,
                                  post_score: undefined,
                                }));
                              }
                              setEnabled((enabled) => ({
                                ...enabled,
                                scoringPostEnabled: e.target.checked,
                              }));
                            }}
                          />
                        }
                        label={t("scoring.post_scoring_enabled")}
                        labelPlacement="end"
                        sx={{
                          "&.MuiFormControlLabel-root": {
                            margin: 0,
                            width: "133px",
                            // justifyContent: "space-between",
                            gap: "2.0625rem",
                            fontWeight: "700",
                          },
                          "& .MuiTypography-root": {
                            fontWeight: "700",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "nowrap",
                          gap: "2.0625rem",
                          alignItems: "center",
                        }}
                      >
                        <InputCst
                          key={"post_score"}
                          id={"post_score"}
                          value={inputForm.post_score}
                          disabled={!enabled.scoringPostEnabled}
                          onChange={(e) => {
                            setInputForm((inputForm: any) => ({
                              ...inputForm,
                              post_score: e.target.value,
                            }));
                          }}
                          label={columnsByAccessorKey?.["post_score"].header}
                          sx={{
                            "& .MuiInputBase-root .MuiInputBase-input": {
                              paddingLeft: theme.spacing(1.875),
                              paddingTop: theme.spacing(1.625),
                              paddingBottom: theme.spacing(1.625),
                              lineHeight: theme.spacing(2.875),
                            },
                            "& .MuiInputLabel-root[data-shrink='false']": {
                              top: "50%",
                              transform: "translateY(-50%)",
                            },
                            "& .MuiFormHelperText-root": {
                              margin: 0,
                            },
                          }}
                          type="number"
                          placeholder={
                            columnsByAccessorKey?.["post_score"].header
                          }
                        />
                        <Typography>
                          {t("scoring.post_scoring_enabled_after")}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      key={"share_score"}
                      item
                      xs={12}
                      sm={12}
                      sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: "6.1875rem",
                        alignContent: "center",
                        marginLeft: "10px",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <MaterialUISwitch
                            key={"scoringShareEnabled"}
                            id={"scoringShareEnabled"}
                            value={enabled.scoringShareEnabled}
                            checked={enabled.scoringShareEnabled}
                            onChange={(e: any) => {
                              if (!e.target.checked) {
                                setInputForm((inputForm: any) => ({
                                  ...inputForm,
                                  share_score: undefined,
                                }));
                              }
                              setEnabled((enabled) => ({
                                ...enabled,
                                scoringShareEnabled: e.target.checked,
                              }));
                            }}
                          />
                        }
                        label={t("scoring.share_scoring_enabled")}
                        labelPlacement="end"
                        sx={{
                          "&.MuiFormControlLabel-root": {
                            margin: 0,
                            width: "133px",
                            // justifyContent: "space-between",
                            gap: "2.0625rem",
                            fontWeight: "700",
                          },
                          "& .MuiTypography-root": {
                            fontWeight: "700",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "nowrap",
                          gap: "2.0625rem",
                          alignItems: "center",
                        }}
                      >
                        <InputCst
                          key={"share_score"}
                          id={"share_score"}
                          value={inputForm.share_score}
                          disabled={!enabled.scoringShareEnabled}
                          onChange={(e) => {
                            setInputForm((inputForm: any) => ({
                              ...inputForm,
                              share_score: e.target.value,
                            }));
                          }}
                          label={columnsByAccessorKey?.["share_score"].header}
                          sx={{
                            "& .MuiInputBase-root .MuiInputBase-input": {
                              paddingLeft: theme.spacing(1.875),
                              paddingTop: theme.spacing(1.625),
                              paddingBottom: theme.spacing(1.625),
                              lineHeight: theme.spacing(2.875),
                            },
                            "& .MuiInputLabel-root[data-shrink='false']": {
                              top: "50%",
                              transform: "translateY(-50%)",
                            },
                            "& .MuiFormHelperText-root": {
                              margin: 0,
                            },
                          }}
                          type="number"
                        />
                        <Typography>
                          {t("scoring.share_scoring_enabled_after")}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      key={"comment_score"}
                      item
                      xs={12}
                      sm={12}
                      sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: "6.1875rem",
                        alignContent: "center",
                        marginLeft: "10px",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <MaterialUISwitch
                            key={"scoringCommentEnabled"}
                            id={"scoringCommentEnabled"}
                            value={enabled.scoringCommentEnabled}
                            checked={enabled.scoringCommentEnabled}
                            onChange={(e: any) => {
                              if (!e.target.checked) {
                                setInputForm((inputForm: any) => ({
                                  ...inputForm,
                                  comment_score: undefined,
                                }));
                              }
                              setEnabled((enabled) => ({
                                ...enabled,
                                scoringCommentEnabled: e.target.checked,
                              }));
                            }}
                          />
                        }
                        label={t("scoring.comment_scoring_enabled")}
                        labelPlacement="end"
                        sx={{
                          "&.MuiFormControlLabel-root": {
                            margin: 0,
                            width: "133px",
                            // justifyContent: "space-between",
                            gap: "2.0625rem",
                            fontWeight: "700",
                          },
                          "& .MuiTypography-root": {
                            fontWeight: "700",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "nowrap",
                          gap: "2.0625rem",
                          alignItems: "center",
                        }}
                      >
                        <InputCst
                          key={"comment_score"}
                          id={"comment_score"}
                          value={inputForm.comment_score}
                          disabled={!enabled.scoringCommentEnabled}
                          onChange={(e) => {
                            setInputForm((inputForm: any) => ({
                              ...inputForm,
                              comment_score: e.target.value,
                            }));
                          }}
                          label={columnsByAccessorKey?.["comment_score"].header}
                          sx={{
                            "& .MuiInputBase-root .MuiInputBase-input": {
                              paddingLeft: theme.spacing(1.875),
                              paddingTop: theme.spacing(1.625),
                              paddingBottom: theme.spacing(1.625),
                              lineHeight: theme.spacing(2.875),
                            },
                            "& .MuiInputLabel-root[data-shrink='false']": {
                              top: "50%",
                              transform: "translateY(-50%)",
                            },
                            "& .MuiFormHelperText-root": {
                              margin: 0,
                            },
                          }}
                          type="number"
                        />
                        <Typography>
                          {t("scoring.comment_scoring_enabled_after")}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid
                      key={"like_score"}
                      item
                      xs={12}
                      sm={12}
                      sx={{
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: "6.1875rem",
                        alignContent: "center",
                        marginLeft: "10px",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <MaterialUISwitch
                            key={"scoringLikeEnabled"}
                            id={"scoringLikeEnabled"}
                            value={enabled.scoringLikeEnabled}
                            checked={enabled.scoringLikeEnabled}
                            onChange={(e: any) => {
                              if (!e.target.checked) {
                                setInputForm((inputForm: any) => ({
                                  ...inputForm,
                                  like_score: undefined,
                                }));
                              }
                              setEnabled((enabled) => ({
                                ...enabled,
                                scoringLikeEnabled: e.target.checked,
                              }));
                            }}
                          />
                        }
                        label={t("scoring.like_scoring_enabled")}
                        labelPlacement="end"
                        sx={{
                          "&.MuiFormControlLabel-root": {
                            margin: 0,
                            width: "133px",
                            // justifyContent: "space-between",
                            gap: "2.0625rem",
                            fontWeight: "700",
                          },
                          "& .MuiTypography-root": {
                            fontWeight: "700",
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "nowrap",
                          gap: "2.0625rem",
                          alignItems: "center",
                        }}
                      >
                        <InputCst
                          key={"like_score"}
                          id={"like_score"}
                          value={inputForm.like_score}
                          disabled={!enabled.scoringLikeEnabled}
                          onChange={(e) => {
                            setInputForm((inputForm: any) => ({
                              ...inputForm,
                              like_score: e.target.value,
                            }));
                          }}
                          label={columnsByAccessorKey?.["like_score"].header}
                          sx={{
                            "& .MuiInputBase-root .MuiInputBase-input": {
                              paddingLeft: theme.spacing(1.875),
                              paddingTop: theme.spacing(1.625),
                              paddingBottom: theme.spacing(1.625),
                              lineHeight: theme.spacing(2.875),
                            },
                            "& .MuiInputLabel-root[data-shrink='false']": {
                              top: "50%",
                              transform: "translateY(-50%)",
                            },
                            "& .MuiFormHelperText-root": {
                              margin: 0,
                            },
                          }}
                          type="number"
                        />
                        <Typography>
                          {t("scoring.like_scoring_enabled_after")}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        mt: "32px",
                        display: "flex",
                        alignItems: "center",
                        gap: "20px",
                        justifyContent: "space-between",
                        [theme.breakpoints.down("sm")]: {
                          flexDirection: "column",
                        },
                      }}
                    >
                      <ButtonCst
                        id={"back-button"}
                        variant={"outlined"}
                        size={"medium"}
                        sx={{
                          minWidth: "150px",
                          mr: "auto",
                          [theme.breakpoints.down("lg")]: {
                            minWidth: "100px",
                          },
                          [theme.breakpoints.down("sm")]: {
                            minWidth: "100%",
                          },
                        }}
                        onClick={() => {
                          navigate(-1);
                        }}
                      >
                        {t("backButton", { ns: "form" })}
                      </ButtonCst>
                      <ButtonCst
                        id={"reset-button"}
                        variant={"outlined"}
                        size={"medium"}
                        sx={{
                          minWidth: "150px",
                          [theme.breakpoints.down("lg")]: {
                            minWidth: "100px",
                          },
                          [theme.breakpoints.down("sm")]: {
                            minWidth: "100%",
                          },
                        }}
                        onClick={resetInputForm}
                      >
                        {t("resetButton", { ns: "form" })}
                      </ButtonCst>
                      <ButtonCst
                        id={"save-button"}
                        variant={"contained"}
                        size={"medium"}
                        sx={{
                          minWidth: "150px",
                          [theme.breakpoints.down("lg")]: {
                            minWidth: "100px",
                          },
                          [theme.breakpoints.down("sm")]: {
                            minWidth: "100%",
                          },
                        }}
                        onClick={() => onConfirm()}
                      >
                        {t("saveButton", { ns: "form" })}
                      </ButtonCst>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}
          {openAssociations && (
            <AssociationTableCst
              association={getValues(
                (openAssociations as Association).accessorKey,
              )}
              setAssociation={(value: any) => {
                handleAssociation(
                  (openAssociations as Association).accessorKey,
                  value,
                );
              }}
              setCloseAssociation={(value: boolean) =>
                setOpenAssociation(
                  (openAssociations as Association).accessorKey,
                  value,
                )
              }
              title={t((openAssociations as Association).titleKey, {
                ns: "form",
              })}
              renderTopToolbar={
                (openAssociations as Association).accessorKey === "communities"
                  ? customRenderTopToolbar
                  : undefined
              }
              service={(openAssociations as { service: string }).service}
              pageID={(openAssociations as { pageId: PagesID }).pageId}
              filter={(openAssociations as Association).accessorKey}
              defaultFilters={(openAssociations as Association).defaultFilters}
              manageConfigs={(openAssociations as Association).manageConfigs}
              enableMultiRowSelection={
                (openAssociations as Association).multiChoice
              }
              labelKey={(openAssociations as Association).labelKey}
              durationKey={false}
            />
          )}
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </>
  );
};

export default ScoringCluster;
